import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, ErrorAlert, Pagination, Spinner, StatCard } from '../components/ui';
import { usePagination } from '../lib/usePagination';

type Any = Record<string, any>;

function fmt(value: unknown): string {
  if (!value) return '—';
  const time = Date.parse(String(value));
  if (!Number.isFinite(time)) return String(value).slice(0, 30);
  return new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'short' }).format(new Date(time));
}

function bridgeTone(status: string): 'neutral' | 'info' | 'warning' | 'success' | 'danger' {
  if (['PUSHED', 'UPDATED', 'DRAFT'].includes(status)) return 'success';
  if (['PENDING', 'REVIEW'].includes(status)) return 'warning';
  if (['FAILED'].includes(status)) return 'danger';
  return 'info';
}

/**
 * Two-way editorial bridge:
 * - Kontentowo article -> NieboGuard public SEO content (review status),
 * - NieboGuard alert -> Kontentowo DRAFT social variants (never published).
 */
export function NieboGuardContentSection() {
  const [status, setStatus] = useState<Any | null>(null);
  const [articles, setArticles] = useState<Any[]>([]);
  const [alerts, setAlerts] = useState<Any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [test, setTest] = useState<Any | null>(null);
  const articlePage = usePagination(articles, 10);
  const alertPage = usePagination(alerts, 10);
  const recentPage = usePagination<Any>(status?.recent ?? [], 10);

  const load = useCallback(async () => {
    setError(null);
    const [nextStatus, nextArticles, nextAlerts] = await Promise.all([
      api.nieboguard.content.status(),
      api.nieboguard.content.articles(),
      api.nieboguard.content.alerts(30),
    ]);
    setStatus(nextStatus);
    setArticles(nextArticles.items ?? []);
    setAlerts(nextAlerts.items ?? []);
  }, []);

  useEffect(() => { load().catch(setError); }, [load]);

  const act = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key); setError(null); setMessage('');
    try {
      const result = await action();
      if (key === 'test') setTest(result as Any);
      setMessage(success);
      await load();
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };

  if (!status) return error ? <ErrorAlert error={error} /> : <Spinner label="Ładowanie mostu treści…" />;
  const configured = status.configured ?? {};
  const counts = status.counts ?? [];
  const pendingOut = counts.filter((row: Any) => row.direction === 'out').reduce((n: number, row: Any) => n + Number(row.n || 0), 0);
  const pendingIn = counts.filter((row: Any) => row.direction === 'in').reduce((n: number, row: Any) => n + Number(row.n || 0), 0);

  return <div className="nb-list">
    {error ? <ErrorAlert error={error} /> : null}
    {message ? <div className="alert alert--success">{message}</div> : null}

    <div className="nb-hero">
      <div className="nb-row nb-row--between">
        <div>
          <h2>Most treści NieboGuard ↔ Kontentowo</h2>
          <p>Artykuł zatwierdzony w Kontentowo trafia do NieboGuard jako treść do przeglądu. Komunikat NieboGuard może utworzyć w Kontentowo szkic postów. Żaden kierunek nie publikuje automatycznie.</p>
        </div>
        <Badge tone={configured.token ? 'success' : 'danger'}>{configured.token ? 'Token mostu ustawiony' : 'Brak tokenu mostu'}</Badge>
      </div>
      <div className="nb-row">
        <Badge tone="info">API: {configured.apiBase ?? '—'}</Badge>
        <Badge tone="neutral">Public: {configured.publicUrl ?? '—'}</Badge>
        <Badge tone={configured.token ? 'success' : 'warning'}>{configured.token ? 'Gotowy do wymiany pakietów' : 'Tylko podgląd, bez wysyłki'}</Badge>
      </div>
      {!configured.token ? <p className="fineprint">Ustaw `NIEBOGUARD_CONTENT_TOKEN` w Workerze Kontentowo i `KONTENTOWO_CONTENT_TOKEN` w Workerze NieboGuard. Wartość musi być identyczna; nie wpisuj jej do repozytorium.</p> : null}
    </div>

    <div className="nb-grid nb-grid--stats">
      <StatCard label="Artykuły gotowe" value={status.articles ?? 0} />
      <StatCard label="Powiązania OUT" value={pendingOut} />
      <StatCard label="Szkice z alertów" value={pendingIn} />
      <StatCard label="Ostatnia aktywność" value={status.recent?.[0]?.updatedAt ? fmt(status.recent[0].updatedAt) : '—'} />
    </div>

    <Card title="Kierunek Kontentowo → NieboGuard" actions={<Button size="sm" variant="ghost" onClick={() => act('refresh', load, 'Odświeżono most treści.')} disabled={busy !== null || !configured.token}>Odśwież</Button>}>
      <div className="nb-stat-note" style={{ marginBottom: 10 }}>Wysyłane są tylko artykuły APPROVED/PUBLISHED, z jednym językiem, źródłami i metadanymi SEO. NieboGuard zapisuje je jako `review`, więc administrator decyduje o publikacji i indeksacji.</div>
      {articles.length === 0 ? <EmptyState title="Brak artykułów do wysłania" hint="Zatwierdź artykuł w sekcji Artykuły/PINS, a potem wróć tutaj." /> :
        <div className="nb-scroll"><table className="table">
          <thead><tr><th>Tytuł</th><th>Język</th><th>Status</th><th>SEO</th><th>Most</th><th>Akcje</th></tr></thead>
          <tbody>{articlePage.pageRows.map((article) => <tr key={article.id}>
            <td><strong>{article.title}</strong><div className="nb-item__meta">{article.slug ?? 'brak slug'} · {article.category ?? 'bez kategorii'}</div></td>
            <td>{article.language}</td>
            <td><Badge tone={article.status === 'PUBLISHED' ? 'success' : 'info'}>{article.status}</Badge></td>
            <td><div className="nb-item__meta">{article.seoTitle ?? 'brak SEO title'}</div><div className="nb-item__meta">{article.sourceCount ?? 0} źródeł</div></td>
            <td>{article.bridge ? <Badge tone={bridgeTone(article.bridge.status)}>{article.bridge.status}</Badge> : <span className="nb-muted">niewysłany</span>}
              {article.bridge?.externalId ? <div className="nb-item__meta"><code>{article.bridge.externalId}</code></div> : null}
              {article.bridge?.lastError ? <div className="nb-item__meta nb-danger">{article.bridge.lastError}</div> : null}
            </td>
            <td><Button size="sm" disabled={!configured.token || busy !== null} onClick={() => act(`push-${article.id}`, () => api.nieboguard.content.pushArticle(article.id), 'Artykuł wysłany do NieboGuard jako treść do przeglądu.')}>{busy === `push-${article.id}` ? 'Wysyłam…' : article.bridge?.status === 'FAILED' ? 'Ponów wysyłkę' : 'Wyślij do NieboGuard'}</Button></td>
          </tr>)}</tbody>
        </table><Pagination page={articlePage.page} pageSize={articlePage.pageSize} total={articlePage.total} onChange={articlePage.setPage} /></div>}
    </Card>

    <Card title="Kierunek NieboGuard → Kontentowo" actions={<><Button size="sm" variant="ghost" disabled={!configured.token || busy !== null} onClick={() => act('test', () => api.nieboguard.content.testConnection(), 'Test połączenia z NieboGuard wykonany.')}>Test połączenia</Button><Button size="sm" variant="ghost" onClick={() => load().catch(setError)}>Odśwież</Button></>}>
      <div className="nb-stat-note" style={{ marginBottom: 10 }}>Pobieramy wyłącznie publiczny eksport komunikatów. „Utwórz szkic social” zakłada element w istniejącym Content Studio, z wariantami DRAFT i źródłem; nie publikuje i nie tworzy wideo.</div>
      {test ? <div className="nb-stat-note">Test: HTTP OK · próbka: {test.sampleCount ?? 0} · {test.sample?.title ?? 'brak komunikatu'}</div> : null}
      {alerts.length === 0 ? <EmptyState title="Brak komunikatów do szkicowania" hint="Eksport NieboGuard nie zwrócił aktualnych komunikatów." /> :
        <div className="nb-scroll"><table className="table">
          <thead><tr><th>Czas</th><th>Komunikat</th><th>Źródło / region</th><th>Formaty</th><th>Akcje</th></tr></thead>
          <tbody>{alertPage.pageRows.map((alert) => <tr key={alert.id}>
            <td className="nb-muted">{fmt(alert.published_at || alert.event_at)}</td>
            <td><strong>{alert.title}</strong><div className="nb-item__meta">{String(alert.body || '').slice(0, 180) || 'brak opisu'}</div><div className="nb-item__meta"><code>{alert.id}</code></div></td>
            <td><div>{alert.source_label ?? alert.source ?? '—'}</div><div className="nb-item__meta">{alert.region ?? 'Polska'} · {alert.verification ?? 'bez weryfikacji'}</div>{alert.url ? <a href={alert.url} target="_blank" rel="noreferrer">NieboGuard ↗</a> : null}</td>
            <td>{(alert.formats ?? []).join(', ')}</td>
            <td><Button size="sm" disabled={!configured.token || busy !== null} onClick={() => act(`draft-${alert.id}`, () => api.nieboguard.content.createAlertDraft(alert.id), 'Szkic social utworzony w Content Studio (DRAFT).')}>{busy === `draft-${alert.id}` ? 'Tworzę…' : 'Utwórz szkic social'}</Button></td>
          </tr>)}</tbody>
        </table><Pagination page={articlePage.page} pageSize={articlePage.pageSize} total={articlePage.total} onChange={articlePage.setPage} /></div>}
    </Card>

    <Card title="Ostatnie powiązania">
      {status.recent?.length ? <div className="nb-scroll"><table className="table"><thead><tr><th>Kierunek</th><th>Lokalne</th><th>Zewnętrzne</th><th>Status</th><th>Aktualizacja</th></tr></thead><tbody>{recentPage.pageRows.map((link: Any) => <tr key={link.id}>
        <td>{link.direction === 'out' ? 'Kontentowo → NieboGuard' : 'NieboGuard → Kontentowo'}</td>
        <td><code>{link.localKind}:{link.localId}</code></td>
        <td>{link.externalUrl ? <a href={link.externalUrl} target="_blank" rel="noreferrer">{link.externalId}</a> : <code>{link.externalId}</code>}{link.lastError ? <div className="nb-danger"><small>{link.lastError}</small></div> : null}</td>
        <td><Badge tone={bridgeTone(link.status)}>{link.status}</Badge></td>
        <td>{fmt(link.updatedAt)}</td>
      </tr>)}</tbody></table><Pagination page={recentPage.page} pageSize={recentPage.pageSize} total={recentPage.total} onChange={recentPage.setPage} /></div> : <EmptyState title="Brak powiązań" hint="Wyślij artykuł lub utwórz szkic z komunikatu." />}
    </Card>
  </div>;
}
